import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { profilesKeys } from "./useProfiles";
import { biensKeys } from "./useBiens";

/** Invoque l'Edge Function `delete-user`, avec extraction propre du message d'erreur. */
async function invokeDeleteUser(body: { user_id: string; dry_run?: boolean; reassign_to?: string }) {
  const { data, error } = await supabase.functions.invoke("delete-user", { body });
  if (error) {
    let detail = error.message;
    const ctx = (error as unknown as { context?: Response }).context;
    if (ctx && typeof ctx.text === "function") {
      try {
        const parsed = JSON.parse(await ctx.text());
        // Erreur métier « biens assignés » : remontée telle quelle pour que l'UI
        // puisse afficher le garde-fou de réassignation.
        if (parsed?.error === "has_assignments") {
          throw Object.assign(new Error("has_assignments"), {
            code: "has_assignments",
            assigned_count: parsed.assigned_count,
          });
        }
        if (parsed?.error) detail = `${error.message} — ${parsed.error}`;
      } catch (e) {
        if (e instanceof Error && (e as { code?: string }).code === "has_assignments") throw e;
        // body non-JSON → garder error.message
      }
    }
    throw new Error(detail);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

/**
 * Nombre de biens assignés à un collaborateur (collaborateurSrs = son id).
 * Sert au garde-fou de suppression : s'il y en a, on exige une réassignation.
 */
export function useAssignedBiensCount(userId: string | null) {
  return useQuery({
    queryKey: ["assigned-biens-count", userId],
    enabled: !!userId,
    staleTime: 0,
    queryFn: async () => {
      const data = await invokeDeleteUser({ user_id: userId!, dry_run: true });
      return (data as { assigned_count: number }).assigned_count ?? 0;
    },
  });
}

/**
 * Supprime un user via l'Edge Function `delete-user`.
 * Vérifie admin-only via JWT côté serveur + protège contre le self-delete.
 * `reassign_to` : id du collaborateur qui récupère les biens avant suppression.
 */
export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { user_id: string; reassign_to?: string }) => {
      const data = await invokeDeleteUser(params);
      return data as { ok: true; user_id: string; reassigned?: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: profilesKeys.list() });
      // Les biens ont pu changer d'owner (collaborateurSrs) → rafraîchir.
      qc.invalidateQueries({ queryKey: biensKeys.all });
    },
  });
}

/**
 * Désactive un prestataire (statut → inactif) au lieu de le supprimer.
 * RPC `admin_deactivate_prestataire` (migration 074) : il disparaît des dropdowns
 * d'assignation, ses missions ELS/PEB en cours sont conservées, et les biens
 * loués/incomplets où il était assigné voient leur champ vidé (→ « - »).
 */
export function useDeactivatePrestataire() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (user_id: string) => {
      const { data, error } = await supabase.rpc("admin_deactivate_prestataire", { p_id: user_id });
      if (error) throw new Error(error.message);
      return data as { ok: true; cleared_peb: number; cleared_ele: number; cleared_els: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: profilesKeys.list() });
      qc.invalidateQueries({ queryKey: biensKeys.all });
    },
  });
}
